/**
 * Скорость, сглаживание и слова про время — общие для прохода и восстановления.
 *
 * Жили в экране агентов, пока экран был один. Восстановление показывает ту же
 * работу с обратным знаком — скачано из хранилища вместо прочитано с диска, —
 * и вторая копия этих хуков разошлась бы с первой на первой же правке.
 *
 * Форма хода у обоих одна: так договорились с командой агента (сообщение 200),
 * `agent.restore.progress` повторяет `agent.run.progress` поле в поле.
 */
import { useEffect, useRef, useState } from 'react';

/** То, что нужно этим хукам от хода работы — прохода или восстановления. */
export interface ProgressLike {
  /** Сетевая и дисковая скорости от приёмника, если он их шлёт (216). */
  netPerSec?: number;
  writtenPerSec?: number;
  filesDone: number;
  bytesRead: number;
  bytesUploaded: number;
  bytesDistinct?: number | undefined;
  readPerSec?: number | undefined;
  uploadedPerSec?: number | undefined;
}

/**
 * Число, которое доводится до значения, а не прыгает к нему.
 *
 * Ход работы приходит раз в две секунды — чаще нельзя, на медиатеке в четыре
 * миллиона файлов сообщения о ходе съели бы канал. Присвоенное значение
 * означает скачок раз в две секунды; доведение даёт то же самое, но взгляду
 * есть за что зацепиться.
 *
 * `live` выключает доведение, и это не украшение: сглаживание НЕ ДОЛЖНО
 * скрывать остановку. Замерший агент, чьи числа ещё несколько секунд ползут,
 * выглядит работающим — а ровный ход сделан ровно затем, чтобы тишине можно
 * было верить.
 */
export function useEased(target: number, live: boolean, pull = 0.28): number {
  const [shown, setShown] = useState(target);
  const current = useRef(target);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    if (!live) {
      current.current = target;
      setShown(target);
      return;
    }
    // Шаг раз в 80 мс, а не каждый кадр.
    //
    // Каждый кадр — это 60 перерисовок в секунду ради числа, которое человек
    // читает раз в секунду. Двенадцати шагов хватает, чтобы движение выглядело
    // плавным, а экрану остаётся впятеро больше времени на всё остальное.
    // Мера не теоретическая: экран стал вязким ровно после того, как
    // сглаживание поехало на кадрах.
    const step = (): void => {
      const next = current.current + (target - current.current) * pull;
      // У последних долей дёргаться незачем: садимся на значение и стоим.
      // Порог относительный — на доле от нуля до единицы «единица» означала бы
      // «всегда попали», и сглаживания не было бы вовсе.
      if (Math.abs(target - next) <= Math.abs(target) * 0.004 + 1e-4) {
        current.current = target;
        setShown(target);
        return;
      }
      current.current = next;
      setShown(next);
      frame.current = setTimeout(step, 80) as unknown as number;
    };
    frame.current = setTimeout(step, 80) as unknown as number;
    return () => { if (frame.current != null) clearTimeout(frame.current); };
  }, [target, live, pull]);

  return shown;
}

/**
 * Скорость по накопленному — окном, а не по соседним замерам.
 *
 * «Залито» растёт скачками: байты прибавляются, когда пак ПРИНЯТ, то есть раз
 * в 64 МиБ. По двум соседним замерам это даёт ноль почти всегда и всплеск раз
 * в минуту — стрелка стояла бы на нуле и дёргалась.
 *
 * Окно в двадцать секунд сглаживает ступени и остаётся честным: это настоящая
 * средняя за последние двадцать секунд, а не придуманное число. Нужна
 * мгновенная — её шлёт агент, и она берётся вперёд этой.
 */
export function windowedRate(
  history: { t: number; read: number; sent: number }[],
  windowMs = 20_000,
): { read: number; sent: number } | null {
  if (history.length < 2) return null;
  const last = history[history.length - 1]!;
  // Самый ранний замер, попадающий в окно; если все свежее — берём первый.
  const first = history.find((x) => last.t - x.t <= windowMs) ?? history[0]!;
  const secs = (last.t - first.t) / 1000;
  if (secs < 1) return null;
  return {
    read: Math.max(0, (last.read - first.read) / secs),
    sent: Math.max(0, (last.sent - first.sent) / secs),
  };
}

/**
 * Насколько сильную инерцию давать доле — решается по самой доле.
 *
 * Инерция нужна не всегда, а только пока доля мечется. У последовательного
 * конвейера она прыгает от нуля к единице: то читаем, то отправляем, — и без
 * сглаживания это мигание. Но если агент начнёт читать и слать одновременно,
 * доля станет ровной, и та же инерция превратится в отставание: экран будет
 * показывать позавчерашнее соотношение просто потому, что мы велели ему
 * тормозить.
 *
 * Поэтому меряем размах последних значений и обращаем его: мечется — тормозим,
 * стоит ровно — не трогаем вовсе.
 */
export function useFlowInertia(share: number): number {
  const history = useRef<number[]>([]);
  const [pull, setPull] = useState(0.12);

  useEffect(() => {
    const h = history.current;
    h.push(share);
    // Восьми значений хватает: при кадре раз в две секунды это последние
    // шестнадцать секунд — достаточно, чтобы заметить смену ритма, и мало,
    // чтобы не помнить то, чего уже нет.
    if (h.length > 8) h.shift();
    if (h.length < 3) return;

    // Размах считаем по двум окнам и берём МЕНЬШИЙ.
    //
    // По одному длинному окну инерция держалась бы ещё шестнадцать секунд
    // после того, как ритм выровнялся: старые всплески сидят в памяти и
    // требуют торможения, которого уже не нужно. Короткое окно отпускает
    // быстро, длинное не даёт отпустить на случайном совпадении двух соседних
    // значений.
    const spread = (xs: number[]): number => Math.max(...xs) - Math.min(...xs);
    const swing = Math.min(spread(h), spread(h.slice(-4)));

    // Размах около единицы — доля бьётся от края к краю, тормозим сильно.
    // Размах около нуля — поток ровный, ведём почти без задержки.
    setPull(Math.min(0.9, Math.max(0.1, 1 - swing)));
  }, [share]);

  return pull;
}

/** Человеческий срок: часы и минуты, без секунд там, где они не значат ничего. */
export function forHuman(secs: number): string {
  if (secs < 90) return `${Math.round(secs)} с`;
  const m = Math.round(secs / 60);
  if (m < 90) return `${m} мин`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (h < 36) return rest > 0 ? `${h} ч ${rest} мин` : `${h} ч`;
  return `${Math.round(h / 24)} сут`;
}

/**
 * Скорость по двум последним сообщениям.
 *
 * Не среднее с начала: владелец смотрит на неё, чтобы понять, что происходит
 * СЕЙЧАС, — а среднее за час прохода этого не показывает. Среднее осталось в
 * подсказке, там оно и отвечает на свой вопрос: успеет ли до утра.
 */
export function useRecentRate(progress: ProgressLike, windowMs = 20_000): {
  read: number; distinct: number; sent: number; files: number; size: number | null;
} | null {
  /*
   * Готовые числа агента лучше наших разностей: промежуток у него известен
   * точно, и обе величины сняты в один момент. Разность оставлена запасным
   * путём — для агентов, которые их ещё не шлют.
   *
   * Окно ведём в любом случае: файлов в секунду и средний размер файла агент
   * не шлёт, а без них низкая скорость чтения выглядит поломкой, хотя чаще
   * всего означает мелкие файлы.
   */
  const window = useRef<{ t: number; read: number; sent: number; distinct: number; files: number }[]>([]);
  const [rate, setRate] = useState<{
    read: number; distinct: number; sent: number; files: number; size: number | null;
  } | null>(null);

  useEffect(() => {
    const now = Date.now();
    const read = progress.bytesRead;
    const distinct = progress.bytesDistinct ?? progress.bytesUploaded;
    const sent = progress.bytesUploaded;
    const files = progress.filesDone;

    const h = window.current;
    h.push({ t: now, read, sent, distinct, files });
    // Хранить нужно всё окно с запасом: кадры приходят раз в две секунды, и
    // тридцатисекундному окну нужно пятнадцать замеров, а не сорок «на глаз».
    if (h.length > Math.max(40, Math.ceil(windowMs / 1000))) h.shift();
    const w = windowedRate(h, windowMs);
    if (!w) return;

    const firstIn = h.find((x) => now - x.t <= windowMs) ?? h[0]!;
    const secs = (now - firstIn.t) / 1000;
    if (secs < 1) return;

    const dFiles = Math.max(0, files - firstIn.files);
    const dRead = Math.max(0, read - firstIn.read);
    /*
     * Скорости приёмника лучше наших разностей, и с 216 их две настоящих.
     *
     * `netPerSec` — сетевая, `writtenPerSec` — дисковая; замерены у него, за
     * известный промежуток, в один момент. Наши окна остаются запасным путём
     * для агентов, которые их не шлют.
     */
    const given = progress.netPerSec != null || progress.writtenPerSec != null
      || (progress.readPerSec != null && progress.uploadedPerSec != null);
    const rp = progress.netPerSec ?? (given ? progress.readPerSec ?? w.read : w.read);
    // Различное в секунду агент отдельно не шлёт — выводим из долей: какая
    // часть прочитанного оказалась новой, такая часть скорости и «различная».
    const share = read > 0 ? distinct / read : 1;

    setRate({
      read: rp,
      distinct: given ? rp * share : Math.max(0, (distinct - firstIn.distinct) / secs),
      sent: progress.writtenPerSec ?? (given ? progress.uploadedPerSec ?? w.sent : w.sent),
      files: dFiles / secs,
      // Средний файл В ОКНЕ, а не за проход. Именно он объясняет, почему при
      // тысяче файлов в секунду скорость в мегабайтах маленькая: у прохода по
      // медиатеке участок крупных файлов и участок кэша отличаются в десятки раз.
      size: dFiles > 0 ? dRead / dFiles : null,
    });
  }, [
    progress.bytesRead, progress.bytesDistinct, progress.bytesUploaded,
    progress.filesDone, progress.readPerSec, progress.uploadedPerSec,
  ]);

  return rate;
}

/**
 * Часы, которые идут сами.
 *
 * Нужны там, где на экране показано «сколько прошло с последнего движения».
 * Такая надпись считается от текущего времени, а перерисовывается компонент
 * только по приходу события — то есть ровно тогда, когда движение ЕСТЬ. Когда
 * его нет, надпись застывает на «сейчас» и уверенно врёт в единственный
 * момент, когда должна была насторожить: боевое восстановление простояло без
 * единого байта две минуты, а экран всё это время показывал «движение сейчас».
 *
 * Тикает только пока `on`: держать таймер ради завершённой работы незачем.
 */
export function useNow(on: boolean, everyMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(t);
  }, [on, everyMs]);
  return now;
}

/** «12 с назад», «3 мин назад» — для надписи о молчании. */
export function agoWords(ms: number): string {
  const secs = Math.round(ms / 1000);
  if (secs < 15) return 'сейчас';
  if (secs < 90) return `${secs} с назад`;
  return `${Math.round(secs / 60)} мин назад`;
}

/**
 * Медленная скорость — для прогноза, а не для стрелки.
 *
 * Стрелке нужна дрожь: по ней видно, что работа живая. Прогнозу дрожь
 * противопоказана — гигабайтный файл подряд, и «осталось 40 минут»
 * превращается в «осталось 8 часов», а через минуту обратно. Владелец по
 * этому числу планирует день, и число, скачущее втрое, хуже отсутствующего.
 *
 * Поэтому окно длинное — пять минут вместо двадцати секунд, — и поверх него
 * медиана: одна яма (пауза на распаковке пака) сдвигает среднее, а медиану
 * нет. Пока замеров мало, честно отдаём null, и прогноз падает на среднюю с
 * начала.
 */
export function useSlowRate(bytes: number, on: boolean): number | null {
  const window = useRef<{ t: number; b: number }[]>([]);
  const [rate, setRate] = useState<number | null>(null);

  useEffect(() => {
    if (!on) return;
    const now = Date.now();
    const h = window.current;
    h.push({ t: now, b: bytes });
    // Пять минут при кадре раз в две секунды — полтораста замеров.
    while (h.length > 200 || (h.length > 2 && now - h[0]!.t > 300_000)) h.shift();
    if (h.length < 5) return;

    // Медиана поинтервальных скоростей, а не одна разность на всё окно:
    // разность на всё окно — это среднее, и яма в нём остаётся.
    const rates: number[] = [];
    for (let i = 1; i < h.length; i += 1) {
      const dt = (h[i]!.t - h[i - 1]!.t) / 1000;
      if (dt >= 0.5) rates.push(Math.max(0, (h[i]!.b - h[i - 1]!.b) / dt));
    }
    if (rates.length < 4) return;
    rates.sort((a, b) => a - b);
    setRate(rates[Math.floor(rates.length / 2)]!);
  }, [bytes, on]);

  return rate;
}

/**
 * Форма потока: как байты идут во времени, а не сколько их в среднем.
 *
 * Средняя скорость отвечает «сколько», но не отвечает «ровно ли», а на боевом
 * восстановлении это оказалось главным вопросом. Замер: из 57 секунд 43 — с
 * нулём байт, потому что приёмник качает пак на 67 МБ за секунду, потом три
 * секунды его разбирает, и так по кругу. Средняя при этом бодрые 16 МБ/с, а
 * канал простаивает три четверти времени.
 *
 * Хук копит ряд по кадрам хода (они приходят каждые две секунды) и считает
 * долю пустых промежутков. Число это не украшение: когда поток выпрямят, оно
 * пойдёт к нулю, и это будет видно на том же экране, где видно проблему.
 */
export function useFlowShape(bytes: number, on: boolean, keep = 60): {
  series: number[];
  idleShare: number | null;
} {
  const history = useRef<{ t: number; b: number }[]>([]);
  const [shape, setShape] = useState<{ series: number[]; idleShare: number | null }>({
    series: [], idleShare: null,
  });

  useEffect(() => {
    if (!on) return;
    const h = history.current;
    h.push({ t: Date.now(), b: bytes });
    if (h.length > keep + 1) h.shift();
    if (h.length < 4) return;

    const series: number[] = [];
    for (let i = 1; i < h.length; i += 1) {
      const dt = (h[i]!.t - h[i - 1]!.t) / 1000;
      series.push(dt > 0 ? Math.max(0, (h[i]!.b - h[i - 1]!.b) / dt) : 0);
    }
    // «Пусто» — это меньше мегабайта в секунду: ноль в чистом виде бывает
    // редко, а полмегабайта на фоне пятидесяти — та же остановка.
    const idle = series.filter((v) => v < 1e6).length;
    setShape({ series, idleShare: series.length >= 8 ? idle / series.length : null });
  }, [bytes, on, keep]);

  return shape;
}

/**
 * Счётчик записи, не сбиваемый проверочным обходом.
 *
 * Поле `files.done` у приёмника значит разное в разных кадрах: в кадре записи
 * это «записано файлов» (2 553), в кадре проверочного обхода — «осмотрено
 * записей» (4 210 688). Кадры приходят вперемешку, и экран, показывавший
 * последнее пришедшее, дёргался между двумя шкалами с разницей в три порядка:
 * доля файлов прыгала со ста процентов на сотые доли и обратно.
 *
 * Помним последнее, что пришло из кадра записи. Обход показывается отдельно и
 * своим числом — он и правда другая работа.
 */
export function useLastWriting(
  progress: { filesDone: number; filesTotal?: number | null },
  inPrep: boolean,
): { done: number; total: number | null } {
  const seen = useRef<{ done: number; total: number | null }>({ done: 0, total: null });
  if (!inPrep) {
    seen.current = { done: progress.filesDone, total: progress.filesTotal ?? null };
  }
  return seen.current;
}
